import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styles from './UserProfileMenu.module.css';

const UserProfileMenu = ({ user }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Click-outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // ESC key handler
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscKey);

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isMenuOpen]);

  // Body scroll lock for mobile drawer
  useEffect(() => {
    if (isMenuOpen && window.innerWidth <= 768) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLinkClick = () => {
    setIsMenuOpen(false);
  };

  return (
    <div className={styles.userProfileMenu} ref={menuRef}>
      <button
        className={styles.userButton}
        onClick={toggleMenu}
        aria-label="User profile menu"
        aria-expanded={isMenuOpen}
        aria-haspopup="true"
      >
        {/* User icon - visible on desktop */}
        <svg
          className={styles.userIconDesktop}
          viewBox="0 0 24 24"
          fill="currentColor"
          width="24"
          height="24"
        >
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
        </svg>
        {/* Hamburger icon - visible on mobile */}
        <svg className={styles.hamburgerIconMobile} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path d="M3 4h18v2H3V4zm0 7h18v2H3v-2zm0 7h18v2H3v-2z" />
        </svg>
      </button>

      {isMenuOpen && (
        <>
          {/* Desktop dropdown menu */}
          <div className={styles.dropdownMenu} role="menu">
            <div className={styles.menuHeader}>{user.name}</div>

            <Link
              to="/profile"
              className={styles.menuItem}
              role="menuitem"
              onClick={handleLinkClick}
            >
              User Profile
            </Link>

            {user?.privileges?.configuration && (
              <Link
                to="/configuration"
                className={styles.menuItem}
                role="menuitem"
                onClick={handleLinkClick}
              >
                Site Configuration
              </Link>
            )}

            <Link
              to="/system"
              className={styles.menuItem}
              role="menuitem"
              onClick={handleLinkClick}
            >
              System Status
            </Link>

            {user?.privileges?.deity && (
              <Link
                to="/manage/users"
                className={styles.menuItem}
                role="menuitem"
                onClick={handleLinkClick}
              >
                User Management
              </Link>
            )}
          </div>

          {/* Mobile drawer (backdrop + drawer panel) */}
          <div className={styles.backdrop} onClick={() => setIsMenuOpen(false)} aria-hidden="true" />

          <div className={styles.mobileDrawer} role="menu">
            <button
              onClick={() => setIsMenuOpen(false)}
              className={styles.closeButton}
              aria-label="Close menu"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className={styles.menuHeader}>{user.name}</div>

            <Link
              to="/profile"
              className={styles.menuItem}
              role="menuitem"
              onClick={handleLinkClick}
            >
              User Profile
            </Link>

            {user?.privileges?.configuration && (
              <Link
                to="/configuration"
                className={styles.menuItem}
                role="menuitem"
                onClick={handleLinkClick}
              >
                Site Configuration
              </Link>
            )}

            <Link
              to="/system"
              className={styles.menuItem}
              role="menuitem"
              onClick={handleLinkClick}
            >
              System Status
            </Link>

            {user?.privileges?.deity && (
              <Link
                to="/manage/users"
                className={styles.menuItem}
                role="menuitem"
                onClick={handleLinkClick}
              >
                User Management
              </Link>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default UserProfileMenu;
